import { CostType, money } from './commission'

/**
 * Commission, v2 — calculated once, at Sold, and never recalculated.
 *
 * This replaces the provisional/final model in `commission.ts`. Winnie's spec, section 0:
 * *"v1 used a provisional-at-Sold / final-at-Settled model with a holdback %, true-up window,
 * and clawback/top-up adjustments. That model is scrapped. It was based on an assumption that
 * turned out to contradict the accounting team's actual Xero journal design."*
 *
 * The one idea worth holding on to, because everything here follows from it: when an estimate
 * turns out to be wrong, the correction goes through the **P&L**, not back through somebody's
 * commission. A salesperson paid in September does not get a letter in November. That is a
 * business decision, made against the Xero journals, and this file's job is to make it true
 * in code rather than to have an opinion about it.
 *
 * `commission.ts` is left in place and still passes its own tests. It is no longer the model
 * the Commission Report runs on. Nothing here reads a holdback, a true-up deadline, or an
 * adjustment, because in this model none of those things exist.
 */

/**
 * What we have behind a cost line.
 *
 * `expenses.bill_status` has always been checked against three values. This union had two,
 * so a cost the database called Missing had nowhere to land and the Commission Report showed
 * it as Received - the one status that means the opposite. Winnie's Commission Report spec,
 * section 2: "Bill status: Received / Estimated / Missing - flags cost lines not yet
 * confirmed by a supplier invoice." qa/commission.mjs fails if this union and the migration
 * ever stop listing the same three.
 *
 *   Received   a supplier invoice is on file
 *   Estimated  accrued at a standard rate, or a super admin override
 *   Missing    a cost we expect, with neither an invoice nor an estimate behind it
 */
export type BillStatus = 'Received' | 'Estimated' | 'Missing'

/** The three, in the order a person would read them: best known to least. */
export const BILL_STATUSES: BillStatus[] = ['Received', 'Estimated', 'Missing']

/**
 * Turn whatever the database holds into one of the three, in ONE place.
 *
 * Every screen that shows a bill status goes through here. The defect this replaces was a
 * ternary written inline in the report service, and an inline mapping is a mapping that can
 * differ from the next screen's.
 *
 * An unrecognised value falls back to Received because that is the column's own default -
 * but Missing and Estimated are matched explicitly first, so neither can ever be swallowed
 * by that fallback.
 */
export function readBillStatus(value: string | null | undefined): BillStatus {
  const v = String(value || '').trim().toLowerCase()
  if (v === 'missing') return 'Missing'
  if (v === 'estimated') return 'Estimated'
  return 'Received'
}

export interface CostLine {
  id?: string | null
  cost_type: CostType | null
  amount: number | null
  bill_status?: BillStatus | string | null
  supplier?: string | null
  invoice_ref?: string | null
  /** True when this line was auto-created as an accrual rather than entered from an invoice. */
  is_accrual?: boolean | null
  /** True once the reversal journal has posted against it. */
  accrual_reversed?: boolean | null
  date_incurred?: string | null
}

/**
 * The cost types a sold vehicle is expected to carry.
 *
 * `PurchasePrice` is expected but never accrued — see `accrualPlan`. Everything outside this
 * list still counts toward landed cost if somebody enters it; the list only says what the
 * system will go looking for when it decides whether an estimate is needed.
 */
export const EXPECTED_COST_TYPES: CostType[] = [
  'PurchasePrice',
  'Inspection',
  'FreightIn',
  'FreightOut',
]

/**
 * Which costs this vehicle is expected to carry.
 *
 * `accrualCandidates` already applied this rule to the chase list — freight arranged by the
 * buying dealer is somebody else's bill, not one of ours that has gone missing — but the gate
 * on commission did not, so the chase list and the Commission Report could disagree about the
 * same car. One rule, in one place, called by both. Two screens sharing a function but fed
 * different inputs is the failure this project has already been bitten by twice.
 */
export function expectedCostTypes(v: { transportArrangedBy?: string | null }): CostType[] {
  const buyerArranged = String(v.transportArrangedBy || '').toLowerCase() === 'dealer'
  return buyerArranged
    ? EXPECTED_COST_TYPES.filter((c) => c !== 'FreightOut')
    : EXPECTED_COST_TYPES.slice()
}
/* --------------------------------------------------------------------------------------
 * 2. Calculation
 * ------------------------------------------------------------------------------------ */

/**
 * Landed cost: every cost line, actual or estimated, added together.
 *
 * No inflow/outflow split. v1 needed the direction because it locked the two sides at
 * different moments; v2 locks once, so the split has nothing left to decide and keeping it
 * would only invite someone to reintroduce a two-stage calculation by accident.
 */
export function landedCost(costLines: CostLine[] | null | undefined): number {
  return money((costLines || []).reduce((sum, l) => sum + (Number(l.amount) || 0), 0))
}

export function netMargin(
  salePrice: number | null | undefined,
  lines: CostLine[] | null | undefined,
): number | null {
  if (salePrice === null || salePrice === undefined) return null
  return money(Number(salePrice) - landedCost(lines))
}

/**
 * Commission on the margin.
 *
 * A negative margin gives a negative commission rather than zero. Flooring it at zero would
 * hide a car sold at a loss inside a report whose whole purpose is to show what each car
 * actually made — and the flag on the row, not a silent clamp, is the right way to say it.
 */
export function commissionAmount(
  margin: number | null | undefined,
  pct: number | null | undefined,
): number | null {
  if (margin === null || margin === undefined) return null
  const p = Number(pct)
  if (!Number.isFinite(p)) return null
  return money(margin * p)
}

/* --------------------------------------------------------------------------------------
 * 3. Accruals at the lock point
 * ------------------------------------------------------------------------------------ */

export interface AccrualPlanLine {
  costType: CostType
  /** 'actual' — an invoice is on file. 'accrue' — estimate it. 'blocked' — cannot do either. */
  action: 'actual' | 'accrue' | 'blocked' | 'not_expected'
  amount: number | null
  reason: string
}

export interface AccrualPlan {
  lines: AccrualPlanLine[]
  /** Cost types that need an estimate and have no configured default. */
  configGaps: CostType[]
  ok: boolean
}

const isReceived = (l: CostLine) => (l.bill_status || 'Received') === 'Received'

/**
 * What the system would do to each expected cost type if commission were struck right now.
 *
 * Read-only and total: every expected cost type gets a line, including the ones that are
 * fine, so the report can show a vehicle's whole cost picture rather than only its problems.
 *
 * Two refusals are deliberate and neither is negotiable:
 *
 *   - **PurchasePrice is never accrued.** A vehicle with no purchase price is an incomplete
 *     deal, not a late invoice, and a "standard" purchase price would invent the largest
 *     number on the record.
 *   - **A missing default blocks rather than defaulting to zero.** Spec section 8: *"block
 *     calculation, surface as a config gap rather than silently using $0."* Zero is the most
 *     dangerous possible estimate — it inflates the margin, and therefore the commission.
 *   - **`expected` may be narrowed by the caller.** Section 3 says "for every *expected*
 *     cost_type on this vehicle", and which costs a vehicle is expected to carry is a fact
 *     about the deal, not about every deal. The clear case is freight-out: on Winnie's own
 *     pipeline, *Sold — Delivered* means the buyer arranged the shipping, so there is no
 *     delivery bill to wait for and demanding one would block the car forever. This module is
 *     given no deal rows, so the caller narrows the list and the rule stays testable here.
 */
export function accrualPlan(
  costLines: CostLine[] | null | undefined,
  defaults?: Partial<Record<CostType, number>> | null,
  expected?: CostType[] | null,
): AccrualPlan {
  const lines = costLines || []
  const table = defaults || {}
  const out: AccrualPlanLine[] = []
  const gaps: CostType[] = []

  for (const ct of expected && expected.length ? expected : EXPECTED_COST_TYPES) {
    const existing = lines.filter((l) => l.cost_type === ct)
    const received = existing.find(isReceived)

    if (received) {
      out.push({
        costType: ct,
        action: 'actual',
        amount: money(Number(received.amount) || 0),
        reason: 'Invoice on file.',
      })
      continue
    }

    if (existing.length > 0) {
      /* A line exists but is already an estimate. It stands; it does not get re-estimated. */
      out.push({
        costType: ct,
        action: 'accrue',
        amount: money(Number(existing[0].amount) || 0),
        reason: 'Already accrued, waiting on the supplier invoice.',
      })
      continue
    }

    if (ct === 'PurchasePrice') {
      out.push({
        costType: ct,
        action: 'blocked',
        amount: null,
        reason:
          'No purchase price on this vehicle. That is an incomplete deal rather than a missing bill, and it is not something to estimate.',
      })
      gaps.push(ct)
      continue
    }

    const d = Number(table[ct])
    if (Number.isFinite(d) && d > 0) {
      out.push({
        costType: ct,
        action: 'accrue',
        amount: money(d),
        reason: 'No invoice yet, so the standard rate is accrued for it.',
      })
    } else {
      out.push({
        costType: ct,
        action: 'blocked',
        amount: null,
        reason:
          'No invoice and no standard rate configured for this cost, so there is nothing defensible to accrue.',
      })
      gaps.push(ct)
    }
  }

  return { lines: out, configGaps: gaps, ok: gaps.length === 0 }
}

export interface CalcGate {
  ok: boolean
  /**
   * The whole vocabulary of refusal.
   *
   * There is no `not_settled`. Spec section 3 strikes commission at Sold and section 9 says
   * so again; what keeps that safe is `config_gap`, not a later lock point.
   */
  code: 'ok' | 'no_sale_price' | 'config_gap' | 'already_locked'
  reason: string
}

/**
 * May commission be struck on this vehicle?
 *
 * `alreadyLocked` is the important one. Spec section 9: *"CommissionRecord.is_locked is never
 * subsequently set to false or recalculated by any code path."* The gate is here, in the pure
 * layer, so that every caller has to pass through it rather than each service remembering.
 */
export function canCalculate(input: {
  salePrice?: number | null
  plan?: AccrualPlan | null
  alreadyLocked?: boolean
}): CalcGate {
  if (input.alreadyLocked) {
    return {
      ok: false,
      code: 'already_locked',
      reason:
        'Commission on this vehicle was calculated and locked. It is not recalculated — a later invoice goes through the P&L, not through the commission.',
    }
  }
  if (input.salePrice === null || input.salePrice === undefined) {
    return {
      ok: false,
      code: 'no_sale_price',
      reason: 'This vehicle has no sale price, so there is no margin to calculate commission on.',
    }
  }
  const plan = input.plan
  if (plan && !plan.ok) {
    return {
      ok: false,
      code: 'config_gap',
      reason:
        'Blocked on ' +
        plan.configGaps.join(', ') +
        ' — each needs either an invoice or a standard rate before commission can be struck. Using $0 would overstate the margin and therefore the commission.',
    }
  }
  return { ok: true, code: 'ok', reason: 'Ready to calculate.' }
}

/* --------------------------------------------------------------------------------------
 * Report signals
 * ------------------------------------------------------------------------------------ */

/**
 * How many of a vehicle's cost lines have no bill behind them at all.
 *
 * Not the same thing as an estimate, and the difference is the point. An estimate is a
 * number somebody stands behind; a missing bill is a cost we know is coming and have not
 * priced. Counted separately so the report can say which it is.
 */
export function missingBillCount(costLines: CostLine[] | null | undefined): number {
  return (costLines || []).filter((l) => readBillStatus(l.bill_status as string) === 'Missing').length
}

/** How many of a vehicle's cost lines rest on an estimate rather than an invoice. */
export function estimatedLineCount(costLines: CostLine[] | null | undefined): number {
  return (costLines || []).filter((l) => readBillStatus(l.bill_status as string) === 'Estimated').length
}

/**
 * The money still sitting as an estimate with no invoice against it.
 *
 * Spec section 7 calls this "the operational health metric" and it is the one number on the
 * report that has nothing to do with commission: it says how much of the ledger is still
 * waiting on paperwork.
 */
export function unreversedAccrualValue(costLines: CostLine[] | null | undefined): number {
  return money(
    (costLines || [])
      .filter((l) => l.bill_status === 'Estimated' && !l.accrual_reversed)
      .reduce((s, l) => s + (Number(l.amount) || 0), 0),
  )
}

/* --------------------------------------------------------------------------------------
 * 3. Reversal timing, and 8. edge cases
 * ------------------------------------------------------------------------------------ */

/**
 * When the accrual reversal posts.
 *
 * In period if the invoice arrives before the month is locked; on the 1st of the month after
 * the lock if it arrives afterwards. Posting into a closed period is not a thing you get to
 * do, and quietly backdating it is how a reconciled month stops reconciling.
 */
export function reversalDate(
  invoiceDate: string | null | undefined,
  monthLockedThrough?: string | null,
): string | null {
  const d = String(invoiceDate || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null
  const lock = String(monthLockedThrough || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(lock)) return d
  if (d > lock) return d
  const ld = new Date(lock + 'T00:00:00Z')
  ld.setUTCDate(1)
  ld.setUTCMonth(ld.getUTCMonth() + 1)
  return ld.toISOString().slice(0, 10)
}

export interface ReversalGate {
  ok: boolean
  code: 'ok' | 'already_reversed' | 'not_an_accrual'
  reason: string
}

/** Spec section 8: a second reversal against the same accrual is blocked, not tolerated. */
export function canReverse(line: CostLine | null | undefined): ReversalGate {
  if (!line) return { ok: false, code: 'not_an_accrual', reason: 'That cost line no longer exists.' }
  if (line.bill_status !== 'Estimated' && !line.is_accrual) {
    return {
      ok: false,
      code: 'not_an_accrual',
      reason: 'This cost was entered from an invoice, so there is no accrual to reverse.',
    }
  }
  if (line.accrual_reversed) {
    return {
      ok: false,
      code: 'already_reversed',
      reason: 'This accrual has already been reversed. Reversing it twice would double-count it.',
    }
  }
  return { ok: true, code: 'ok', reason: 'Ready to reverse.' }
}

/**
 * Was the estimate wildly wrong?
 *
 * Informational only — spec section 8 is explicit that this never blocks and never touches
 * commission. It exists so somebody notices that the standard rate needs revisiting, which is
 * the only lever left once commission itself stops moving.
 */
export function estimateWayOff(
  estimate: number | null | undefined,
  actual: number | null | undefined,
  factor = 3,
): boolean {
  const e = Number(estimate)
  const a = Number(actual)
  if (!Number.isFinite(e) || !Number.isFinite(a) || e <= 0) return false
  return a >= e * factor || a * factor <= e
}

/** Spec section 8: the same supplier invoice number twice on one vehicle is worth a warning. */
export function duplicateInvoiceRefs(costLines: CostLine[] | null | undefined): string[] {
  const seen: Record<string, number> = {}
  for (const l of costLines || []) {
    const r = String(l.invoice_ref || '').trim().toUpperCase()
    if (!r) continue
    seen[r] = (seen[r] || 0) + 1
  }
  return Object.keys(seen).filter((k) => seen[k] > 1)
}
